import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useData } from '../context/DataContext';
import { MapPin, ExternalLink, Umbrella, Navigation, Waves, Eye, ChevronDown } from 'lucide-react';
import { QuickViewModal } from './QuickViewModal';
import { BookingRequestModal } from './BookingRequestModal';
import { FavoriteButton } from './FavoriteButton';
import { CardSkeleton } from './CardSkeleton';
import { useIsMobile } from '../hooks/useIsMobile';

const BeachClubCard = ({ club, language, t, onQuickView, onBooking }) => {
  const inactive = club.is_active === false;
  const [isFlipped, setIsFlipped] = useState(false);
  return (
  <div
    className={`flip-card ${inactive ? 'inactive-card' : ''} ${isFlipped ? 'flipped' : ''}`}
    data-testid={`beach-club-card-${club.id}`}
    onClick={(e) => {
      if (inactive) return;
      if (e.target.closest('a') || e.target.closest('button') || e.target.closest('.location-link')) return;
      setIsFlipped(!isFlipped);
    }}
  >
    <div className={`flip-card-inner ${inactive ? 'pointer-events-none' : ''}`}>
      {/* Front of Card */}
      <div className={`flip-card-front bg-white border border-stone-100 shadow-sm rounded-2xl flex flex-col`}>
        {/* Discount Badge */}
        {!inactive && club.discount_percent && (
          <div className="absolute top-6 right-6 z-10 bg-brand-slate text-white text-xs font-bold px-3 py-1.5 rounded-full">
            {t('offers.save')} {club.discount_percent}%
          </div>
        )}
        {inactive && (
          <div className="absolute top-6 right-6 z-10 bg-stone-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
            Coming Soon
          </div>
        )}

        {/* Image */}
        <div className="h-56 flex-shrink-0 overflow-hidden rounded-t-2xl relative m-3 mb-0">
          <img loading="lazy"
            src={club.image}
            alt={club.name}
            className="w-full h-full object-cover object-center transition-transform duration-500 rounded-xl"
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&h=600&fit=crop'; }}
          />
          {/* Beach Club Icon Overlay */}
          <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1.5">
            <Umbrella className="w-4 h-4 text-stone-600" />
            <span className="text-xs font-medium text-stone-700">Beach Club</span>
          </div>
          {/* Quick View Button */}
          {!inactive && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onQuickView(club);
            }}
            className="absolute top-3 left-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-stone-600 hover:text-stone-900 hover:bg-white transition-all shadow-sm"
            title="Quick View"
            data-testid={`beach-club-quick-view-${club.id}`}
          >
            <Eye className="w-4 h-4" />
          </button>
          )}
          {/* Favorite Button */}
          {!inactive && (
            <FavoriteButton 
              item={{ id: club.id, type: 'beach_club', name: club.name, image: club.image, location: club.location }}
              className="absolute bottom-3 right-3 z-10"
            />
          )}
        </div>

        {/* Content */}
        <div className="p-5 pt-4 flex flex-col flex-1">
          {/* Nearest Golf Tag - fixed-height row */}
          <div className="flex items-center gap-2 mb-2 min-h-[26px]">
            {club.nearest_golf && (
              <span className="text-xs px-2 py-1 bg-stone-100 text-stone-600 font-medium rounded flex items-center gap-1">
                <Navigation className="w-3 h-3" />
                {club.distance_km}km to {club.nearest_golf}
              </span>
            )}
          </div>
          
          <div 
            className="location-link flex items-center gap-2 text-stone-400 text-xs mb-2 cursor-pointer hover:text-brand-slate transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(club.full_address || club.name + ', ' + club.location + ', Mallorca')}`, '_blank');
            }}
            title="Open in Google Maps"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>{club.location}</span>
          </div>

          <h3 className="font-heading text-xl text-stone-900 mb-2 line-clamp-2">
            {club.name}
          </h3>

          <p className="text-stone-500 text-sm mb-4 line-clamp-2">
            {(club.description && club.description[language]) || (club.description && club.description.en) || ""}
          </p>

          {/* Spacer pushes deal + hover hint to the bottom for layout consistency */}
          <div className="mt-auto">
            {/* Deal Preview */}
            {club.deal && (
              <div className="bg-stone-50 border border-stone-100 rounded-lg p-3 mb-3">
                <p className="text-xs font-medium text-stone-600 line-clamp-1">
                  {(club.deal && club.deal[language]) || (club.deal && club.deal.en) || ""}
                </p>
              </div>
            )}

            {/* Hover hint (desktop) / Tap hint (móvil) */}
            {!inactive && (
            <p className="text-xs text-stone-400 text-center italic">
              <span className="hidden md:inline">{t('card.hoverForDetails') || 'Hover for details'}</span>
              <span className="md:hidden">Tap for details →</span>
            </p>
            )}
          </div>
        </div>
      </div>

      {/* Back of Card */}
      <div className={`flip-card-back rounded-2xl p-6 flex flex-col justify-between text-white`} style={{ background: 'linear-gradient(135deg, #2D2D2D 0%, #3D3D3D 100%)' }}>
        {inactive ? (
          <div className="flex flex-col items-center justify-center h-full">
            <h3 className="text-white text-xl font-heading font-semibold mb-4">{club.name}</h3>
            <p className="text-white/70 text-sm text-center">This partner is not yet available.</p>
            <p className="text-white/50 text-xs mt-2 text-center">Check back soon for availability.</p>
          </div>
        ) : (
        <>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Waves className="w-5 h-5 text-stone-300" />
            <h3 className="text-white text-xl font-heading font-semibold">
              {club.name}
            </h3>
          </div>
          
          <p className="text-stone-300 text-sm leading-relaxed mb-4">
            {(club.description && club.description[language]) || (club.description && club.description.en) || ""}
          </p>

          {/* Location - Clickable */}
          <div 
            className="location-link flex items-center gap-2 text-stone-300 text-sm mb-4 cursor-pointer hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(club.full_address || club.name + ', ' + club.location + ', Mallorca')}`, '_blank');
            }}
            title="Open in Google Maps"
          >
            <MapPin className="w-4 h-4" />
            <span>{club.location}</span>
          </div>

          {/* Deal */}
          {club.deal && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-4">
              <p className="text-white text-xs uppercase tracking-wider mb-1 opacity-80">{t('offers.exclusive')}</p>
              <p className="text-white font-semibold text-sm">
                {(club.deal && club.deal[language]) || (club.deal && club.deal.en) || ""}
              </p>
            </div>
          )}

          {/* Nearest Golf Course */}
          {club.nearest_golf && (
            <div className="flex items-center gap-2 text-stone-300 text-sm">
              <Navigation className="w-4 h-4" />
              <span>{club.distance_km}km from {club.nearest_golf}</span>
            </div>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={(e) => { e.stopPropagation(); onBooking(club); }}
          className="w-full py-3 mt-4 bg-white text-stone-800 text-center rounded-xl font-semibold hover:bg-stone-100 transition-colors flex items-center justify-center gap-2"
        >
          {t('offers.bookNow')}
          <ExternalLink className="w-4 h-4" />
        </button>
        </>
        )}
      </div>
    </div>
  </div>
  );
};

export const BeachClubPartners = () => {
  const { t, language } = useLanguage();
  const { beachClubs, loading, getDisplayedItems } = useData();
  const [quickViewItem, setQuickViewItem] = useState(null);
  const [bookingItem, setBookingItem] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();
  const MOBILE_LIMIT = 6;

  // Apply display limit
  const displayedClubs = getDisplayedItems(beachClubs, 'beach_clubs');
  const visibleClubs = (isMobile && !expanded && displayedClubs.length > MOBILE_LIMIT)
    ? displayedClubs.slice(0, MOBILE_LIMIT)
    : displayedClubs;
  const hasMore = isMobile && !expanded && displayedClubs.length > MOBILE_LIMIT;

  if (loading) {
    return (
      <section className="section-padding bg-brand-cream">
        <div className="container-custom">
          <div className="text-center mb-8">
            <div className="h-4 bg-stone-200 rounded w-40 mx-auto mb-4 animate-pulse" />
            <div className="h-10 bg-stone-200 rounded w-64 mx-auto mb-4 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(isMobile ? 3 : 6)].map((_, i) => <CardSkeleton key={`beachclub-skeleton-${i}`} />)}
          </div>
        </div>
      </section>
    );
  }

  if (beachClubs.length === 0) {
    return null;
  }

  return (
    <>
      <section id="beach-clubs" className="section-padding bg-brand-cream" data-testid="beach-clubs-section">
        <div className="container-custom">
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Umbrella className="w-6 h-6 text-stone-500" />
              <span className="text-sm uppercase tracking-[0.2em] text-stone-500 font-medium">
                {t('beachClubs.subtitle') || 'Relax After Golf'}
              </span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-stone-900 mb-4">
              {t('beachClubs.title') || 'Premium Beach Clubs'}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('beachClubs.description') || 'Unwind after your round at Mallorca\'s most exclusive beach clubs with special golfer packages.'}
            </p>
          </div>

          {/* Beach Club Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleClubs.map((club) => (
              <BeachClubCard 
                key={club.id} 
                club={club} 
                language={language}
                t={t}
                onQuickView={setQuickViewItem}
                onBooking={setBookingItem}
              />
            ))}
          </div>

          {/* View All button - mobile only */}
          {hasMore && (
            <div className="text-center mt-8">
              <button
                onClick={() => setExpanded(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-stone-200 rounded-full text-sm font-medium text-stone-600 hover:border-stone-400 hover:text-stone-800 transition-colors shadow-sm"
                data-testid="beach-clubs-view-all"
              >
                View all {displayedClubs.length} beach clubs
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Quick View Modal */}
      <QuickViewModal
        isOpen={!!quickViewItem}
        onClose={() => setQuickViewItem(null)}
        item={quickViewItem}
        type="beach_club"
        language={language}
        t={t}
        onBooking={(item) => { setQuickViewItem(null); setBookingItem(item); }}
      />

      {/* Booking Request Modal */}
      <BookingRequestModal
        isOpen={!!bookingItem}
        onClose={() => setBookingItem(null)}
        venue={bookingItem}
        venueType="beach_club"
      />
    </>
  );
};

export default BeachClubPartners;
